<template>
  <div class="weeduc-admin-page weeduc-integrations-page text-start p-0">
    <header class="weeduc-admin-page__header">
      <div class="weeduc-admin-page__header-lead">
        <TitlePage
          :title="$t('admin.apiIntegrations.title')"
          :subtitle="$t('admin.apiIntegrations.subtitle')"
          :hasMargin="false"
        />
      </div>
    </header>

    <div class="weeduc-admin-page__shell">
      <section class="weeduc-admin-page__panel weeduc-integrations-panel weeduc-integrations-panel--token">
        <div class="weeduc-integrations-token-layout">
          <div>
            <h2 class="weeduc-integrations-panel__title">
              {{ $t('admin.apiIntegrations.tokenTitle') }}
            </h2>
            <p class="weeduc-integrations-panel__lead">
              {{ $t('admin.apiIntegrations.tokenLead') }}
            </p>
            <p class="weeduc-integrations-panel__lead">
              <a
                class="weeduc-integrations-docs"
                :href="docsUrl"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ $t('admin.apiIntegrations.docsLink') }}
              </a>
            </p>

            <div class="weeduc-integrations-token">
              <label class="weeduc-integrations-label" for="apiIntegrationToken">
                {{ $t('admin.apiIntegrations.apiToken') }}
              </label>
              <div class="weeduc-integrations-token__row">
                <input
                  id="apiIntegrationToken"
                  class="weeduc-integrations-token__input"
                  type="text"
                  readonly
                  :value="displayedToken"
                  :placeholder="$t('admin.apiIntegrations.tokenEmpty')"
                  autocomplete="off"
                />
                <button
                  type="button"
                  class="weeduc-integrations-btn weeduc-integrations-btn--ghost"
                  :title="tokenVisible ? $t('admin.apiIntegrations.hideToken') : $t('admin.apiIntegrations.showToken')"
                  :disabled="!apiToken"
                  @click="tokenVisible = !tokenVisible"
                >
                  <fa :icon="tokenVisible ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'" />
                </button>
                <button
                  type="button"
                  class="weeduc-integrations-btn weeduc-integrations-btn--ghost"
                  :title="apiToken ? $t('admin.apiIntegrations.copyToken') : $t('admin.apiIntegrations.copyUnavailable')"
                  :disabled="!apiToken"
                  @click="copyToken"
                >
                  <fa icon="fa-solid fa-copy" />
                </button>
              </div>
              <p class="weeduc-integrations-hint">
                {{ tokenHint }}
              </p>
            </div>

            <div class="weeduc-integrations-actions">
              <button
                type="button"
                class="weeduc-integrations-btn weeduc-integrations-btn--primary"
                :disabled="busy"
                @click="regenerateToken"
              >
                {{ busy === 'regen' ? $t('admin.apiIntegrations.generating') : $t('admin.apiIntegrations.regenerate') }}
              </button>
              <button
                type="button"
                class="weeduc-integrations-btn weeduc-integrations-btn--danger"
                :disabled="busy || !keyActive"
                @click="revokeToken"
              >
                {{ busy === 'revoke' ? $t('admin.apiIntegrations.revoking') : $t('admin.apiIntegrations.revoke') }}
              </button>
            </div>
          </div>

          <div class="weeduc-integrations-meta">
            <div>
              <span>{{ $t('admin.apiIntegrations.status') }}</span>
              <strong :class="keyActive ? 'is-active' : 'is-inactive'">
                {{ keyActive ? $t('admin.apiIntegrations.statusActive') : $t('admin.apiIntegrations.statusInactive') }}
              </strong>
            </div>
            <div>
              <span>{{ $t('admin.apiIntegrations.createdAt') }}</span>
              <strong>{{ createdAtLabel }}</strong>
            </div>
            <div>
              <span>{{ $t('admin.apiIntegrations.lastUsed') }}</span>
              <strong>{{ lastUsedLabel }}</strong>
            </div>
            <div v-if="scopes.length">
              <span>{{ $t('admin.apiIntegrations.scopes') }}</span>
              <strong class="weeduc-integrations-scopes">{{ scopes.join(' · ') }}</strong>
            </div>
          </div>
        </div>
      </section>

      <section class="weeduc-admin-page__panel weeduc-integrations-panel">
        <nav
          class="weeduc-admin-tabs"
          :aria-label="$t('admin.apiIntegrations.tabsAria')"
        >
          <button
            v-for="tab in tabs"
            :key="tab.id"
            type="button"
            class="weeduc-admin-tab"
            :class="{ 'weeduc-admin-tab--active': activeTab === tab.id }"
            @click="activeTab = tab.id"
          >
            {{ tab.label }}
          </button>
        </nav>

        <div
          v-for="tab in tabs"
          :key="`panel-${tab.id}`"
          v-show="activeTab === tab.id"
          class="weeduc-integrations-tab-panel"
        >
          <h2 class="weeduc-integrations-panel__title">
            {{ tab.title }}
          </h2>
          <p class="weeduc-integrations-panel__lead">
            {{ tab.lead }}
          </p>

          <div class="weeduc-integrations-endpoint-cards">
            <article
              v-for="endpoint in tab.endpoints"
              :key="endpoint.id"
              class="weeduc-integrations-endpoint-card"
              :class="{ 'is-selected': selectedEndpointId === endpoint.id }"
              @click="selectedEndpointId = endpoint.id"
            >
              <div class="weeduc-integrations-endpoint-card__head">
                <span
                  class="weeduc-integrations-method"
                  :data-method="endpoint.method"
                >
                  {{ endpoint.method }}
                </span>
                <span
                  class="weeduc-integrations-resource__badge"
                  :data-resource="endpoint.resource"
                >
                  {{ resourceLabel(endpoint.resource) }}
                </span>
              </div>

              <p class="weeduc-integrations-endpoint-card__name">
                {{ endpoint.name }}
              </p>
              <p class="weeduc-integrations-endpoint-card__desc">
                {{ endpoint.description }}
              </p>

              <div class="weeduc-integrations-endpoint__row">
                <code class="weeduc-integrations-code">{{ endpoint.path }}</code>
                <button
                  type="button"
                  class="weeduc-integrations-btn weeduc-integrations-btn--ghost"
                  :title="$t('admin.apiIntegrations.copyEndpoint')"
                  @click.stop="copyText(endpoint.path, 'endpoint')"
                >
                  <fa icon="fa-solid fa-copy" />
                </button>
              </div>

              <div
                v-if="endpoint.queryExample"
                class="weeduc-integrations-body"
              >
                <p class="weeduc-integrations-label">
                  {{ $t('admin.apiIntegrations.queryExample') }}
                </p>
                <pre class="weeduc-integrations-pre">{{ endpoint.queryExample }}</pre>
              </div>
              <div
                v-if="endpoint.bodyExample"
                class="weeduc-integrations-body"
              >
                <p class="weeduc-integrations-label">
                  {{ $t('admin.apiIntegrations.bodyExample') }}
                </p>
                <pre class="weeduc-integrations-pre">{{ endpoint.bodyExample }}</pre>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section class="weeduc-admin-page__panel weeduc-integrations-panel weeduc-integrations-panel--table">
        <div class="weeduc-integrations-table-head">
          <div>
            <h2 class="weeduc-integrations-panel__title">
              {{ $t('admin.apiIntegrations.historyTitle') }}
            </h2>
            <p class="weeduc-integrations-panel__lead">
              {{ $t('admin.apiIntegrations.historyLead') }}
            </p>
          </div>
          <span class="weeduc-integrations-badge">{{ $t('admin.apiIntegrations.historyBadge') }}</span>
        </div>

        <div class="weeduc-admin-page__table-section">
          <v-table class="custom-table">
            <thead>
              <tr>
                <th>{{ $t('admin.apiIntegrations.colDate') }}</th>
                <th>{{ $t('admin.apiIntegrations.colResource') }}</th>
                <th>{{ $t('admin.apiIntegrations.colMethod') }}</th>
                <th>{{ $t('admin.apiIntegrations.colRecords') }}</th>
                <th>{{ $t('admin.apiIntegrations.colStatus') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in requestHistory" :key="item.id">
                <td>{{ item.date }}</td>
                <td>
                  <span class="weeduc-integrations-resource__badge" :data-resource="item.resource">
                    {{ resourceLabel(item.resource) }}
                  </span>
                </td>
                <td>
                  <span class="weeduc-integrations-method" :data-method="item.method">
                    {{ item.method }}
                  </span>
                </td>
                <td>{{ item.records }}</td>
                <td>
                  <span
                    class="weeduc-integrations-status"
                    :class="isSuccessStatus(item.status) ? 'is-success' : 'is-error'"
                  >
                    {{ item.status }}
                  </span>
                </td>
              </tr>
              <tr v-if="!requestHistory.length">
                <td colspan="5" class="text-center text-medium-emphasis">
                  {{ $t('admin.apiIntegrations.historyEmpty') }}
                </td>
              </tr>
            </tbody>
          </v-table>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import TitlePage from '@/components/Weflix/titlePage.vue';
import { toast } from 'vue3-toastify';
import integrationKeys, { integrationApiBase } from '@/utils/integrations/keys';

function formatDateTime(value) {
  if (!value) return '—';
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(value instanceof Date ? value : new Date(value));
  } catch {
    return String(value);
  }
}

function prettyJson(value) {
  return JSON.stringify(value, null, 2);
}

export default {
  name: 'WeeducAdminIntegrationsApi',
  components: { TitlePage },
  data() {
    return {
      apiToken: '',
      keyActive: false,
      keyPrefix: '',
      scopes: [],
      tokenVisible: false,
      createdAt: null,
      lastUsed: null,
      activeTab: 'users',
      selectedEndpointId: 'import-users',
      busy: null,
      requestHistory: [],
    };
  },
  computed: {
    ...mapState(['token']),
    docsUrl() {
      return `${integrationApiBase()}/swagger-ui/index.html?urls.primaryName=integracao`;
    },
    displayedToken() {
      if (this.apiToken) {
        return this.tokenVisible ? this.apiToken : this.maskedToken;
      }
      if (this.keyPrefix) return `${this.keyPrefix}…`;
      return '';
    },
    tokenHint() {
      if (this.apiToken) return this.$t('admin.apiIntegrations.tokenHintSecret');
      if (this.keyActive) return this.$t('admin.apiIntegrations.tokenHintActive');
      if (this.keyPrefix) return this.$t('admin.apiIntegrations.tokenHintRevoked');
      return this.$t('admin.apiIntegrations.tokenHintEmpty');
    },
    maskedToken() {
      if (!this.apiToken) return '';
      const visible = this.apiToken.slice(0, 10);
      return `${visible}${'•'.repeat(Math.max(12, this.apiToken.length - 10))}`;
    },
    createdAtLabel() {
      return formatDateTime(this.createdAt);
    },
    lastUsedLabel() {
      return formatDateTime(this.lastUsed);
    },
    tabs() {
      const base = integrationApiBase();
      const path = (suffix) => `${base}${suffix}`;
      return [
        {
          id: 'users',
          label: this.$t('admin.apiIntegrations.tabUsers'),
          title: this.$t('admin.apiIntegrations.tabUsersTitle'),
          lead: this.$t('admin.apiIntegrations.tabUsersLead'),
          endpoints: [
            {
              id: 'import-users',
              resource: 'users',
              method: 'POST',
              name: this.$t('admin.apiIntegrations.epImportUsers'),
              description: this.$t('admin.apiIntegrations.epImportUsersDesc'),
              path: path('/integrations/users/import'),
              bodyExample: prettyJson({
                users: [
                  {
                    externalId: 'colab-100',
                    name: 'Ana Souza',
                    email: 'ana@empresa.com',
                    phone: '11999999999',
                    type: 'collaborator',
                    status: 'active',
                  },
                ],
              }),
            },
            {
              id: 'users-list',
              resource: 'users',
              method: 'GET',
              name: this.$t('admin.apiIntegrations.epUsersList'),
              description: this.$t('admin.apiIntegrations.epUsersListDesc'),
              path: path('/integrations/users'),
              queryExample: 'type=student&status=active&search=&page=1&pageSize=100',
            },
            {
              id: 'users-organization',
              resource: 'organization',
              method: 'POST',
              name: this.$t('admin.apiIntegrations.epOrganization'),
              description: this.$t('admin.apiIntegrations.epOrganizationDesc'),
              path: path('/integrations/users/organization'),
              bodyExample: prettyJson({
                email: 'ana@empresa.com',
                sectorId: 'token-do-setor',
                roleId: 'token-do-cargo',
                responsible: false,
              }),
            },
          ],
        },
        {
          id: 'courses',
          label: this.$t('admin.apiIntegrations.tabCourses'),
          title: this.$t('admin.apiIntegrations.tabCoursesTitle'),
          lead: this.$t('admin.apiIntegrations.tabCoursesLead'),
          endpoints: [
            {
              id: 'import-courses',
              resource: 'courses',
              method: 'POST',
              name: this.$t('admin.apiIntegrations.epImportCourses'),
              description: this.$t('admin.apiIntegrations.epImportCoursesDesc'),
              path: path('/integrations/courses/import'),
              bodyExample: prettyJson({
                courses: [
                  {
                    externalId: 'curso-10',
                    name: 'Integração',
                    status: 'internal',
                    shortDescription: 'Curso interno',
                  },
                ],
              }),
            },
            {
              id: 'courses-list',
              resource: 'courses',
              method: 'GET',
              name: this.$t('admin.apiIntegrations.epCoursesList'),
              description: this.$t('admin.apiIntegrations.epCoursesListDesc'),
              path: path('/integrations/courses'),
              queryExample: 'status=internal&search=&page=1&pageSize=100',
            },
          ],
        },
        {
          id: 'enrolments',
          label: this.$t('admin.apiIntegrations.tabEnrolments'),
          title: this.$t('admin.apiIntegrations.tabEnrolmentsTitle'),
          lead: this.$t('admin.apiIntegrations.tabEnrolmentsLead'),
          endpoints: [
            {
              id: 'enrolments-create',
              resource: 'enrolments',
              method: 'POST',
              name: this.$t('admin.apiIntegrations.epEnrolments'),
              description: this.$t('admin.apiIntegrations.epEnrolmentsDesc'),
              path: path('/integrations/enrolments'),
              bodyExample: prettyJson({
                email: 'ana@empresa.com',
                courseExternalId: 'curso-10',
              }),
            },
          ],
        },
        {
          id: 'structure',
          label: this.$t('admin.apiIntegrations.tabStructure'),
          title: this.$t('admin.apiIntegrations.tabStructureTitle'),
          lead: this.$t('admin.apiIntegrations.tabStructureLead'),
          endpoints: [
            {
              id: 'org-units',
              resource: 'organization',
              method: 'GET',
              name: this.$t('admin.apiIntegrations.epOrgUnits'),
              description: this.$t('admin.apiIntegrations.epOrgUnitsDesc'),
              path: path('/integrations/organizational-units'),
              queryExample: 'type=sector',
            },
            {
              id: 'roles-list',
              resource: 'organization',
              method: 'GET',
              name: this.$t('admin.apiIntegrations.epRoles'),
              description: this.$t('admin.apiIntegrations.epRolesDesc'),
              path: path('/integrations/roles'),
              queryExample: 'search=',
            },
          ],
        },
        {
          id: 'reports',
          label: this.$t('admin.apiIntegrations.tabReports'),
          title: this.$t('admin.apiIntegrations.tabReportsTitle'),
          lead: this.$t('admin.apiIntegrations.tabReportsLead'),
          endpoints: [
            {
              id: 'reports-enrolments',
              resource: 'reports',
              method: 'GET',
              name: this.$t('admin.apiIntegrations.epReportsEnrolments'),
              description: this.$t('admin.apiIntegrations.epReportsEnrolmentsDesc'),
              path: path('/integrations/reports/enrolments'),
              queryExample: 'courseId=&status=active&search=&dateFrom=2026-01-01&dateTo=2026-12-31&page=1&pageSize=100',
            },
            {
              id: 'reports-progress',
              resource: 'reports',
              method: 'GET',
              name: this.$t('admin.apiIntegrations.epReportsProgress'),
              description: this.$t('admin.apiIntegrations.epReportsProgressDesc'),
              path: path('/integrations/reports/progress'),
              queryExample: 'dateFrom=2026-01-01&dateTo=2026-12-31&page=1&pageSize=100',
            },
          ],
        },
      ];
    },
  },
  created() {
    this.loadIntegration();
  },
  watch: {
    activeTab(tabId) {
      const tab = this.tabs.find((item) => item.id === tabId);
      if (tab?.endpoints?.length) {
        this.selectedEndpointId = tab.endpoints[0].id;
      }
    },
  },
  methods: {
    resourceLabel(id) {
      const map = {
        users: 'resourceUsers',
        courses: 'resourceCourses',
        enrolments: 'resourceEnrolments',
        organization: 'resourceOrganization',
        reports: 'resourceReports',
      };
      const key = map[id];
      return key ? this.$t(`admin.apiIntegrations.${key}`) : id;
    },
    isSuccessStatus(status) {
      const code = Number(status);
      return code >= 200 && code < 300;
    },
    applyKey(view, { keepSecret = false } = {}) {
      const next = view && typeof view === 'object' ? view : {};
      this.keyActive = next.active === true;
      this.keyPrefix = next.prefix || '';
      this.createdAt = next.createdAt || null;
      this.lastUsed = next.lastUsedAt || null;
      this.scopes = Array.isArray(next.scopes) ? next.scopes : [];
      if (next.secret) {
        this.apiToken = next.secret;
        this.tokenVisible = true;
        return;
      }
      if (!keepSecret) {
        this.apiToken = '';
        this.tokenVisible = false;
      }
    },
    async loadIntegration() {
      if (!this.token) return;
      try {
        const [key, requests] = await Promise.all([
          integrationKeys.getKey(this.token),
          integrationKeys.listRequests(this.token),
        ]);
        this.applyKey(key);
        this.requestHistory = requests.map((item, index) => ({
          id: `${item.createdAt || index}-${item.method}-${item.resource}`,
          date: formatDateTime(item.createdAt),
          resource: item.resource,
          method: item.method,
          records: item.records ?? 0,
          status: item.status,
        }));
      } catch (error) {
        console.error('Falha ao carregar a integração:', error);
        toast.error(this.$t('admin.apiIntegrations.loadError'));
      }
    },
    async copyToken() {
      if (!this.apiToken) return;
      await this.copyText(this.apiToken, 'token');
    },
    async copyText(value, kind) {
      try {
        await navigator.clipboard.writeText(value);
        toast.success(
          kind === 'token'
            ? this.$t('admin.apiIntegrations.copySuccess')
            : this.$t('admin.apiIntegrations.copyEndpointSuccess'),
        );
      } catch {
        toast.error(this.$t('admin.apiIntegrations.copyError'));
      }
    },
    async regenerateToken() {
      if (this.keyActive && !window.confirm(this.$t('admin.apiIntegrations.regenConfirm'))) {
        return;
      }
      this.busy = 'regen';
      try {
        const key = await integrationKeys.generateKey(this.token);
        this.applyKey(key);
        toast.success(this.$t('admin.apiIntegrations.regenSuccess'));
      } catch (error) {
        console.error('Falha ao gerar a chave de integração:', error);
        toast.error(this.$t('admin.apiIntegrations.regenError'));
      } finally {
        this.busy = null;
      }
    },
    async revokeToken() {
      if (!window.confirm(this.$t('admin.apiIntegrations.revokeConfirm'))) return;
      this.busy = 'revoke';
      try {
        const key = await integrationKeys.revokeKey(this.token);
        this.applyKey(key);
        toast.info(this.$t('admin.apiIntegrations.revokeSuccess'));
      } catch (error) {
        console.error('Falha ao revogar a chave de integração:', error);
        toast.error(this.$t('admin.apiIntegrations.revokeError'));
      } finally {
        this.busy = null;
      }
    },
  },
};
</script>

<style scoped>
@import '@/css/weeduc/admin/dashboard-weflix-shell.css';

.weeduc-integrations-panel--token {
  margin-bottom: 1.15rem;
}

.weeduc-integrations-token-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(220px, 0.8fr);
  gap: 1.5rem;
  align-items: start;
}

.weeduc-integrations-panel__title {
  margin: 0 0 0.35rem;
  font-size: 1.05rem;
  font-weight: 700;
  color: #1a1d26;
}

.weeduc-integrations-panel__lead {
  margin: 0 0 1.15rem;
  font-size: 0.875rem;
  color: #697386;
  line-height: 1.45;
}

.weeduc-integrations-label {
  display: block;
  margin-bottom: 0.4rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
}

.weeduc-integrations-token__row,
.weeduc-integrations-endpoint__row {
  display: flex;
  gap: 0.5rem;
  align-items: stretch;
}

.weeduc-integrations-token__input {
  flex: 1;
  min-width: 0;
  padding: 0.7rem 0.9rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.8125rem;
  color: #1f2937;
  background: #f9fafb;
}

.weeduc-integrations-hint {
  margin: 0.45rem 0 0;
  font-size: 0.75rem;
  color: #6b7280;
  line-height: 1.4;
}

.weeduc-integrations-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  margin-top: 1rem;
}

.weeduc-integrations-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 0.55rem 0.95rem;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;
}

.weeduc-integrations-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.weeduc-integrations-btn--primary {
  background: #250891;
  color: #fff !important;
  -webkit-text-fill-color: #fff;
}

.weeduc-integrations-btn--primary:hover:not(:disabled) {
  background: #1d0673;
}

.weeduc-integrations-btn--danger {
  background: #fff;
  color: #b91c1c;
  border-color: #fecaca;
}

.weeduc-integrations-btn--danger:hover:not(:disabled) {
  background: #fef2f2;
}

.weeduc-integrations-btn--ghost {
  background: #fff;
  color: #250891;
  border-color: #d1d5db;
  min-width: 2.5rem;
  padding-left: 0.7rem;
  padding-right: 0.7rem;
}

.weeduc-integrations-btn--ghost:hover:not(:disabled) {
  background: #f3f0ff;
  border-color: #250891;
  color: #1d0673;
}

.weeduc-integrations-meta {
  display: grid;
  gap: 0.85rem;
  padding: 1rem;
  border: 1px solid #e8eaf0;
  border-radius: 10px;
  background: #f9fafb;
}

.weeduc-integrations-meta span {
  display: block;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #9ca3af;
  margin-bottom: 0.2rem;
}

.weeduc-integrations-meta strong {
  font-size: 0.875rem;
  color: #1f2937;
  font-weight: 600;
}

.weeduc-integrations-meta strong.is-active {
  color: #047857;
}

.weeduc-integrations-meta strong.is-inactive {
  color: #b91c1c;
}

.weeduc-integrations-scopes {
  display: block;
  font-weight: 600;
  line-height: 1.45;
  word-break: break-word;
}

.weeduc-integrations-docs {
  color: #250891;
  font-weight: 600;
}

.weeduc-integrations-tab-panel {
  margin-top: 1.15rem;
}

.weeduc-integrations-endpoint-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 0.95rem;
}

.weeduc-integrations-endpoint-card {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  padding: 1rem;
  border: 1px solid #e8eaf0;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  text-align: left;
}

.weeduc-integrations-endpoint-card.is-selected {
  border-color: #250891;
  background: #f8f7ff;
}

.weeduc-integrations-endpoint-card__head {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  align-items: center;
}

.weeduc-integrations-endpoint-card__name {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2937;
}

.weeduc-integrations-endpoint-card__desc {
  margin: 0;
  font-size: 0.8125rem;
  color: #697386;
  line-height: 1.4;
}

.weeduc-integrations-endpoint-card__actions {
  margin-top: auto;
  padding-top: 0.35rem;
}

.weeduc-integrations-method {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.5rem;
  border-radius: 6px;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  background: #dcfce7;
  color: #166534;
}

.weeduc-integrations-method[data-method='GET'] {
  background: #dbeafe;
  color: #1d4ed8;
}

.weeduc-integrations-resource__badge {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  background: #f3f4f6;
  color: #4b5563;
}

.weeduc-integrations-resource__badge[data-resource='clients'],
.weeduc-integrations-resource__badge[data-resource='users'] {
  background: #ede9fe;
  color: #5b21b6;
}

.weeduc-integrations-resource__badge[data-resource='courses'] {
  background: #dbeafe;
  color: #1d4ed8;
}

.weeduc-integrations-resource__badge[data-resource='students'] {
  background: #ccfbf1;
  color: #0f766e;
}

.weeduc-integrations-resource__badge[data-resource='collaborators'] {
  background: #ffedd5;
  color: #c2410c;
}

.weeduc-integrations-resource__badge[data-resource='reports'] {
  background: #fce7f3;
  color: #be185d;
}

.weeduc-integrations-code {
  display: block;
  flex: 1;
  min-width: 0;
  padding: 0.7rem 0.9rem;
  border-radius: 8px;
  background: #111827;
  color: #e5e7eb;
  font-size: 0.72rem;
  word-break: break-all;
}

.weeduc-integrations-body {
  margin-top: 0.15rem;
}

.weeduc-integrations-pre {
  margin: 0;
  padding: 0.7rem 0.85rem;
  border-radius: 8px;
  background: #f3f4f6;
  color: #374151;
  font-size: 0.72rem;
  line-height: 1.45;
  overflow-x: auto;
}

.weeduc-integrations-table-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.35rem;
}

.weeduc-integrations-badge {
  flex-shrink: 0;
  padding: 0.25rem 0.55rem;
  border-radius: 999px;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.weeduc-integrations-status {
  display: inline-flex;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.weeduc-integrations-status.is-success {
  background: #d1fae5;
  color: #047857;
}

.weeduc-integrations-status.is-error {
  background: #fee2e2;
  color: #b91c1c;
}

.weeduc-integrations-panel--table {
  margin-top: 1.15rem;
}

@media (max-width: 960px) {
  .weeduc-integrations-token-layout {
    grid-template-columns: 1fr;
  }
}
</style>
